/**
 * The purchase-order dialogs: a manual order, its header, and its lines.
 *
 * Functional spec: `_new_po_manual`, `_POMetaDialog` and `_POLineDialog` in
 * `qt_poc/po_qt.py`.
 *
 * Three things carried over from the desktop that are easy to get wrong here:
 *
 *   1. **A manual order is created server-side** (`POST /pos/manual`), not by
 *      posting a PO. `purchase_orders.bom_id` is NOT NULL and the order's
 *      supplier is resolved through that BOM's dealer, so a manual PO needs an
 *      anchor BOM created with it — two writes that belong in one place.
 *   2. **`discount_pct` is a fraction (0–1); `vat_pct` and `shipping_pct` are
 *      on the 0–100 scale.** They sit next to each other in the same dialog and
 *      look alike. Getting it backwards moves a total by a factor of 100.
 *   3. **Reducing a line's quantity below what has already been received**
 *      clamps the received figure. The desktop asks first, because the
 *      alternative is a receipt record quietly rewritten.
 */
import { useEffect, useState } from 'react'
import {
  Alert, Button, Group, NumberInput, Select, SimpleGrid, Stack, Text,
  TextInput, Textarea,
} from '@mantine/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'

import { ApiError, api } from '../api/client'
import type { Dealer, POLine, PurchaseOrder } from '../api/types'
import { Modal } from '../components/AppModal'
import { fmtMoney } from '../format'
import { confirmAction } from '../components/confirm'

// `ComponentType` in database/models.py — the stored values, not labels.
const COMPONENT_TYPES = ['Hardware', 'License', 'Support', 'Accessories']

export function ManualPOModal({
  opened, onClose, onCreated,
}: {
  opened: boolean
  onClose: () => void
  onCreated: (po: PurchaseOrder) => void
}) {
  const qc = useQueryClient()
  const [dealerId, setDealerId] = useState<string | null>(null)
  const [project, setProject] = useState('')
  const [error, setError] = useState<string | null>(null)

  const dealers = useQuery({
    queryKey: ['dealers'],
    queryFn: () => api.get<Dealer[]>('/dealers'),
    enabled: opened,
  })

  useEffect(() => {
    if (opened) { setDealerId(null); setProject(''); setError(null) }
  }, [opened])

  const create = useMutation({
    mutationFn: () =>
      api.post<PurchaseOrder>('/pos/manual', {
        dealer_id: Number(dealerId), project_name: project.trim(),
      }),
    onSuccess: (po) => {
      void qc.invalidateQueries({ queryKey: ['pos'] })
      void qc.invalidateQueries({ queryKey: ['boms'] })
      notifications.show({ message: `הזמנה ${po.po_number} נוצרה`, color: 'green' })
      onCreated(po)
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'יצירת ההזמנה נכשלה.'),
  })

  const noDealers = dealers.isSuccess && dealers.data.length === 0

  return (
    <Modal opened={opened} onClose={onClose} title="הזמנה ידנית">
      <Stack>
        {error && <Alert color="red" variant="light">{error}</Alert>}
        {noDealers && (
          <Alert color="yellow" variant="light">
            אין ספקים במערכת. הוסף ספק תחילה דרך הגדרות → ספקים.
          </Alert>
        )}
        <Select
          label="ספק"
          required
          searchable
          placeholder="בחר ספק"
          data={(dealers.data ?? []).map((d) => ({ value: String(d.id), label: d.name }))}
          value={dealerId}
          onChange={setDealerId}
        />
        <TextInput
          label="שם פרויקט"
          required
          value={project}
          onChange={(e) => setProject(e.currentTarget.value)}
        />
        <Text size="xs" c="dimmed">
          ההזמנה תיווצר ריקה. הוסף לה שורות במסך הפרטים.
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>ביטול</Button>
          <Button
            loading={create.isPending}
            disabled={!dealerId || !project.trim()}
            onClick={() => { setError(null); create.mutate() }}
          >
            צור
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

const META: { key: keyof PurchaseOrder; label: string }[] = [
  { key: 'order_ref', label: 'אסמכתא ספק' },
  { key: 'contract_number', label: 'מספר חוזה' },
  { key: 'requisition_number', label: 'מספר דרישה' },
  { key: 'internal_owner', label: 'בעלים פנימי' },
  { key: 'vendor_contact', label: 'איש קשר דילר' },
  { key: 'order_number', label: 'מספר הזמנה' },
  { key: 'maor_number', label: 'מספר מאור' },
  { key: 'emf_number', label: 'מספר EMF' },
  { key: 'belam_number', label: 'מספר בל"מ' },
]

export function POHeaderModal({
  po, onClose, onSave, saving,
}: {
  po: PurchaseOrder | null
  onClose: () => void
  onSave: (next: PurchaseOrder) => void
  saving: boolean
}) {
  const [draft, setDraft] = useState<PurchaseOrder | null>(null)
  const opened = po !== null

  useEffect(() => { if (po) setDraft(structuredClone(po)) }, [po])

  function set<K extends keyof PurchaseOrder>(key: K, value: PurchaseOrder[K]) {
    setDraft((d) => (d ? { ...d, [key]: value } : d))
  }

  if (!draft) return <Modal opened={false} onClose={onClose} children={null} />

  return (
    <Modal opened={opened} onClose={onClose} size="lg" title={`עריכת הזמנה ${draft.po_number}`}>
      <Stack>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <TextInput
            label="שם פרויקט"
            value={draft.project_name}
            onChange={(e) => set('project_name', e.currentTarget.value)}
          />
          {META.map(({ key, label }) => (
            <TextInput
              key={key}
              label={label}
              value={(draft[key] as string) ?? ''}
              onChange={(e) => set(key, e.currentTarget.value as never)}
            />
          ))}
          <TextInput
            label="תאריך הזמנה" type="date" value={draft.order_date}
            onChange={(e) => set('order_date', e.currentTarget.value)}
          />
          <TextInput
            label="אספקה משוערת" type="date" value={draft.expected_delivery}
            onChange={(e) => set('expected_delivery', e.currentTarget.value)}
          />
          <TextInput
            label="אספקה בפועל" type="date" value={draft.actual_delivery}
            onChange={(e) => set('actual_delivery', e.currentTarget.value)}
          />
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          {/* 0–100 scale, like the BOM's. */}
          <NumberInput
            label='מע"מ %' hideControls min={0} max={100} decimalScale={2}
            value={draft.vat_pct}
            onChange={(v) => set('vat_pct', Number(v) || 0)}
          />
          <NumberInput
            label="שילוח %" hideControls min={0} max={100} decimalScale={2}
            value={draft.shipping_pct}
            onChange={(v) => set('shipping_pct', Number(v) || 0)}
          />
          {/* Stored as a fraction, unlike the two beside it. */}
          <NumberInput
            label="הנחה %" hideControls min={0} max={100} decimalScale={2}
            value={Number((draft.discount_pct * 100).toFixed(2))}
            onChange={(v) => set('discount_pct', (Number(v) || 0) / 100)}
          />
        </SimpleGrid>

        <Textarea
          label="הערות" autosize minRows={2} value={draft.notes}
          onChange={(e) => set('notes', e.currentTarget.value)}
        />
        <Text size="xs" c="dimmed">
          הסטטוס ואחוז הקבלה נגזרים מכמויות השורות ואינם ניתנים לעריכה כאן.
        </Text>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>ביטול</Button>
          <Button loading={saving} onClick={() => onSave(draft)}>שמור</Button>
        </Group>
      </Stack>
    </Modal>
  )
}

/** `null` line = add a new one. */
export function POLineModal({
  line, opened, onClose, onSave,
}: {
  line: POLine | null
  opened: boolean
  onClose: () => void
  onSave: (line: POLine) => void
}) {
  const [draft, setDraft] = useState<POLine | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!opened) return
    setError(null)
    setDraft(line ? { ...line } : {
      id: null, po_id: null, sku: '', internal_sku: '', description: '',
      component_type: 'Hardware', qty_to_order: 1, qty_received: 0,
      received_date: '', unit_price_usd: 0,
    })
  }, [opened, line])

  function set<K extends keyof POLine>(key: K, value: POLine[K]) {
    setDraft((d) => (d ? { ...d, [key]: value } : d))
  }

  async function submit() {
    if (!draft) return
    setError(null)
    if (!draft.sku.trim()) return setError('יש להזין מק"ט.')
    if (draft.qty_to_order <= 0) return setError('כמות להזמנה חייבת להיות גדולה מ-0.')
    let next = draft
    if (draft.qty_received > draft.qty_to_order) {
      // Not a deletion, but it silently rewrites a number the user typed, so
      // it is not `danger` red and it says exactly what it will change.
      const ok = await confirmAction({
        title: 'התאמת כמות שהתקבלה',
        message: `הכמות שהתקבלה (${draft.qty_received}) גדולה מהכמות החדשה `
          + `(${draft.qty_to_order}). הכמות שהתקבלה תותאם. להמשיך?`,
        confirmLabel: 'המשך',
        danger: false,
      })
      if (!ok) return
      next = { ...draft, qty_received: draft.qty_to_order }
    }
    onSave(next)
  }

  if (!draft) return null

  return (
    <Modal opened={opened} onClose={onClose} title={line ? 'עריכת שורה' : 'שורה חדשה'}>
      <Stack>
        {error && <Alert color="red" variant="light">{error}</Alert>}
        <Group grow>
          <TextInput
            label='מק"ט יצרן' required value={draft.sku}
            onChange={(e) => set('sku', e.currentTarget.value)}
          />
          <TextInput
            label='מק"ט פנימי' value={draft.internal_sku}
            onChange={(e) => set('internal_sku', e.currentTarget.value)}
          />
        </Group>
        <TextInput
          label="תיאור" value={draft.description}
          onChange={(e) => set('description', e.currentTarget.value)}
        />
        <Group grow>
          <Select
            label="סוג" data={COMPONENT_TYPES} value={draft.component_type}
            onChange={(v) => set('component_type', v ?? 'Hardware')}
          />
          <NumberInput
            label="כמות" hideControls min={0} decimalScale={2} value={draft.qty_to_order}
            onChange={(v) => set('qty_to_order', Number(v) || 0)}
          />
          <NumberInput
            label="מחיר יחידה USD" hideControls min={0} decimalScale={2} thousandSeparator=","
            value={draft.unit_price_usd}
            onChange={(v) => set('unit_price_usd', Number(v) || 0)}
          />
        </Group>
        <Text size="sm" c="dimmed">
          סה"כ שורה: {fmtMoney(draft.qty_to_order * draft.unit_price_usd)}
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>ביטול</Button>
          <Button onClick={() => void submit()}>שמור</Button>
        </Group>
      </Stack>
    </Modal>
  )
}
